import { BoltOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { SlideAIModelModal } from 'components/AITools/components/SlideAIModelModal';
import { SlideAIModelSelectableItem } from 'components/AITools/components/SlideAIModelSelectableItem';
import {
  AI_MODEL_CHANGE_ACTIONS,
  AI_MODELS,
  MODELS_CONTAINER_ID,
} from 'components/AITools/constants/common';
import { useContentStyles } from 'components/AITools/constants/styles';
import {
  isModelExists,
  isQCModel,
  serializeAiModelOptions,
} from 'components/AITools/utilities/common';
import { PercentageSlider } from 'components/Shared/PercantageSlider';
import { APP_LIBRARY } from 'constants/urls';
import logo from 'images/AITools/histowiz.png';
import aiAnalyzeProgressLogo from 'images/AITools/ai_tools_analyzing_progress.gif';
import { DownloadIcon } from 'images/icons/DownloadIcon';
import { memo, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addCurrentSlideAIModel,
  removeCurrentSlideAIModel,
  selectCurrentSlideAIModels,
  setLastChangedAIModelOptions,
  setMLDataLoadingMap,
} from 'store/slices/aiToolsSlice';
import {
  forceOpenModelSelector,
  setForceOpenModel,
} from 'store/slices/slidesListSlice/slidesListSlice';
import { isStaffSelector } from 'store/slices/userDetailsSlice';
import { useModelQuantifications } from 'components/AITools/hooks/useModelQuantifications';
import { ShowNotDemoUserOnly } from 'components/ConditionalWrappers/IsNotDemoUserWrapper/ShowNotDemoUserOnly';
import { useExportSlidesQuantification } from 'components/AITools/hooks/useExportSlidesQuantification';
import { useDownloadAnnotations } from 'components/AITools/hooks/useDownloadAnnotations';
import { useIsMLAnalyzeInProgress } from 'components/AITools/hooks/useIsMLAnalyzeInProgress';

export const ToolsContent = ({
  slide,
  isToolSelected = false,
  slidesTableProps,
  model,
  opacityOptions,
  isDisabled,
  mlModelsClassName,
}) => {
  const { classes } = useContentStyles();
  const forceOpenModel = useSelector(forceOpenModelSelector);
  const currentSlideAIModels = useSelector(selectCurrentSlideAIModels);
  const [modelModalOpen, setModelModalOpen] = useState('');
  const isStaff = useSelector(isStaffSelector);
  const [isExpanded, setIsExpanded] = useState(false);
  const dispatch = useDispatch();

  const { aiModels } = useModelQuantifications(
    model,
    slide,
    isExpanded,
    opacityOptions,
  );

  const slides = useMemo(
    () =>
      slidesTableProps?.selectedSlides.length
        ? slidesTableProps?.selectedSlides
        : [slide],
    [slide, slidesTableProps],
  );

  const { isAnalyzeInProgress, setAnalysisLocallyStarted } =
    useIsMLAnalyzeInProgress(slides, model.id);

  const { isFetching, downloadAnnotations } = useDownloadAnnotations({
    modelId: model.id,
    slides,
  });

  const { isExportingQuantification, exportSlidesQuantification } =
    useExportSlidesQuantification({
      model,
      slides,
      opacityOptions,
    });

  const serializedModelOptions = useMemo(
    () => serializeAiModelOptions({ model, aiModels, opacityOptions }),
    [model, aiModels, opacityOptions],
  );

  const checkIfModelSelected = (modelName) =>
    currentSlideAIModels.includes(modelName);

  const handleAIModelChange = (modelName) => () => {
    let changeType = AI_MODEL_CHANGE_ACTIONS.ADD;
    const isQC = isQCModel(modelName);

    if (!checkIfModelSelected(modelName)) {
      dispatch(addCurrentSlideAIModel(modelName));
      if (!isQC) {
        // necessary to avoid blinking of tick before spinner
        dispatch(setMLDataLoadingMap({ modelName, isLoading: true }));
      }
    } else {
      dispatch(removeCurrentSlideAIModel(modelName));
      changeType = 'remove';
    }

    dispatch(setLastChangedAIModelOptions({ modelName, changeType }));
  };

  const handleModelModalOpen = (id, e) => {
    e.stopPropagation();
    setModelModalOpen(id);
  };

  const handleModelModalClose = () => {
    setModelModalOpen('');
  };

  const handleLearnMore = (e) => {
    e.stopPropagation();
    window.open(`${APP_LIBRARY}/${model.id}`, 'blank');
  };

  useEffect(() => {
    if (forceOpenModel === model.id) {
      setModelModalOpen(forceOpenModel);
      dispatch(setForceOpenModel(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpenModel]);

  useEffect(() => {
    if (isToolSelected && model.id) {
      const container = document.getElementById(MODELS_CONTAINER_ID);
      const selectedModelPosition = document.getElementById(model.id).offsetTop;
      container.scrollTo({
        top: selectedModelPosition - 85,
        behavior: 'smooth',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToolSelected]);

  const isModelExistsForAll = useMemo(
    () => slides.every((currentSlide) => isModelExists(currentSlide, model)),
    [model, slides],
  );

  useEffect(() => {
    if (isStaff && isToolSelected) {
      setIsExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToolSelected]);

  // eslint-disable-next-line no-nested-ternary
  const runButtonLabel = isAnalyzeInProgress
    ? 'Analyzing'
    : isModelExistsForAll
      ? 'Order Details'
      : 'Run Model';

  return (
    <Grid
      container
      id={model.id}
      direction="column"
      className={isDisabled ? classes.disabledContent : ''}
    >
      {modelModalOpen && (
        <SlideAIModelModal
          slides={slidesTableProps?.slides}
          selectedSlides={slides}
          open={modelModalOpen}
          isProcessed={isModelExistsForAll}
          onClose={handleModelModalClose}
          onRunStart={() => setAnalysisLocallyStarted(true)}
          model={model}
        />
      )}
      <Accordion
        className={classes.accordion}
        disableGutters
        expanded={isExpanded}
        onChange={(e, expanded) => setIsExpanded(expanded)}
      >
        <AccordionSummary className={classes.accordionSummary}>
          <Box className={`${classes.mlModels} ${mlModelsClassName}`}>
            <Typography className={classes.mlModelsHeader}>
              {model?.title}{' '}
              {/* TODO: remove after management addes aiosyn app page */}
              {model?.id !== AI_MODELS.aiosyn && (
                <Typography
                  component="span"
                  className={classes.learnMore}
                  onClick={handleLearnMore}
                >
                  Learn More
                </Typography>
              )}
            </Typography>
            <Box>
              <Box display="flex" justifyContent="center">
                <img
                  src={model.logo || logo}
                  alt="logo"
                  className={classes.logo}
                />
              </Box>
            </Box>
            <PercentageSlider
              className={classes.percentageSliderWrapper}
              value={opacityOptions.opacity}
              handleChange={opacityOptions.handleChange}
            />
            <ShowNotDemoUserOnly>
              <Button
                color="secondary"
                variant={isModelExists ? 'outlined' : 'contained'}
                startIcon={isModelExists ? null : <BoltOutlined />}
                endIcon={
                  isAnalyzeInProgress && (
                    <img
                      src={aiAnalyzeProgressLogo}
                      alt="Analyzing"
                      className={classes.analyzingGif}
                    />
                  )
                }
                className={classes.runButton}
                sx={{
                  pointerEvents: isAnalyzeInProgress ? 'none' : 'auto',
                  padding: isModelExists
                    ? '0.625rem 2.68rem'
                    : '0.625rem 2.06rem',
                }}
                onClick={(e) => handleModelModalOpen(model.id, e)}
              >
                {runButtonLabel}
              </Button>
            </ShowNotDemoUserOnly>
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Grid
            item
            container
            spacing={2}
            className={classes.aiModelsListWrapper}
          >
            <Grid
              container
              item
              xs={12}
              className={classes.optionsList}
              spacing={2}
            >
              <ShowNotDemoUserOnly>
                <Box className={classes.buttonsContent}>
                  <Button
                    className={classes.downloadButton}
                    endIcon={<DownloadIcon />}
                    color="secondary"
                    variant="contained"
                    onClick={downloadAnnotations}
                    disabled={
                      slides.some((item) => !isModelExists(item, model)) ||
                      isFetching
                    }
                  >
                    <Typography className={classes.downloadText}>
                      {`Download ANNOTATIONS${
                        slides.length > 1 ? ` (${slides.length})` : ''
                      }`}
                    </Typography>
                  </Button>
                  {model.id === AI_MODELS.aira && (
                    <Button
                      className={classes.downloadButton}
                      color="secondary"
                      variant="contained"
                      onClick={exportSlidesQuantification}
                      disabled={
                        slides.some((item) => !isModelExists(item, model)) ||
                        isExportingQuantification
                      }
                    >
                      <Typography className={classes.downloadText}>
                        {`Export Quantifications${
                          slides.length > 1 ? ` (${slides.length})` : ''
                        }`}
                      </Typography>
                    </Button>
                  )}
                </Box>
              </ShowNotDemoUserOnly>
              {serializedModelOptions.map(
                ({
                  name,
                  color,
                  value,
                  quantification,
                  isDisabled: isDisabledOption,
                }) => (
                  <Grid
                    key={name}
                    item
                    xs={12}
                    className={classes.gridWrapperItem}
                  >
                    <SlideAIModelSelectableItem
                      color={color}
                      key={value}
                      slideName={slide.name}
                      quantification={slides.length > 1 ? null : quantification}
                      name={name}
                      isSelected={checkIfModelSelected(value)}
                      handleChange={handleAIModelChange(value)}
                      isDisabled={isDisabledOption}
                    />
                  </Grid>
                ),
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

export const SlideAIToolsContent = memo(ToolsContent);
